import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { fetchProducts } from "../../functions/product";
import { shopList } from "../../functions/shop";
import Loader from "../Loader";
import PageLayout from "../PageLayout";
import ProductSearchConfiguration from "./ProductSearchConfiguration";
import ProductSearchResultDisplayer from "./ProductSearchResultDisplayer";

const LoadingStatus = Object.freeze({
    'LOADED': 'LOADED',
    'LOADING': 'LOADING'
});

export const PARAM_SEARCH_VALUE = "search_value";

function ProductSearcher({ token, onError }) {
    const { t } = useTranslation('messages');

    const [searchParams] = useSearchParams();
    let searchValueParam = searchParams.get(PARAM_SEARCH_VALUE);

    const [searchValue, setSearchValue] = useState("");
    const [products, setProducts] = useState([]);
    const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.LOADED);
    const [checkedShops, setCheckedShops] = useState([]);
    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const [onlyAvailableChecked, setOnlyAvailableChecked] = useState(false);
    const [maxResults, setMaxResults] = useState(10);
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(500);
    const [hasSearched, setHasSearched] = useState(false);

    useEffect(() =>
        searchFromQueryString(
            token, searchValueParam, shopList,
            setCheckedShops, setSelectAllChecked, setOnlyAvailableChecked,
            setSearchValue, searchProducts, setProducts,
            setLoadingStatus, setHasSearched, onError),
        [token, searchValueParam, onError]
    );

    return (
        <PageLayout
            hero={
                <>
                    <h1 className="display-6 fw-bold mb-4">{t('main.store_search.title')}</h1>
                    <ProductSearchConfiguration
                        fetchProducts={() =>
                            searchProducts(token, searchValue, checkedShops, onlyAvailableChecked, maxResults, minPrice, maxPrice, setProducts, setLoadingStatus, setHasSearched, onError)
                        }
                        shopList={shopList}
                        checkedShops={checkedShops}
                        setCheckedShops={setCheckedShops}
                        selectAllChecked={selectAllChecked}
                        setSelectAllChecked={setSelectAllChecked}
                        onlyAvailableChecked={onlyAvailableChecked}
                        setOnlyAvailableChecked={setOnlyAvailableChecked}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        maxResults={maxResults}
                        setMaxResults={setMaxResults}
                        minPrice={minPrice}
                        setMinPrice={setMinPrice}
                        maxPrice={maxPrice}
                        setMaxPrice={setMaxPrice}
                    />
                </>
            }
        >
            {loadingStatus === LoadingStatus.LOADING ? (
                <Row className="justify-content-center py-5">
                    <Col xs="auto">
                        <Loader />
                    </Col>
                </Row>
            ) : (
                <ProductSearchResultDisplayer products={products} hasSearched={hasSearched} />
            )}
        </PageLayout>
    );
}

function searchFromQueryString(
    token, searchValueParam, shopList,
    setCheckedShops, setSelectAllChecked, setOnlyAvailableChecked,
    setSearchValue, searchProducts, setProducts,
    setLoadingStatus, setHasSearched, onError) {
    if (!searchValueParam) return;
    let shopIds = shopList.map(shop => shop.id);
    setCheckedShops(shopIds);
    setSelectAllChecked(true);
    setOnlyAvailableChecked(true);
    setSearchValue(searchValueParam);
    searchProducts(token, searchValueParam, shopIds, true, 10, 0, 500, setProducts, setLoadingStatus, setHasSearched, onError);
}

function searchProducts(token, searchValue, checkedShops, onlyAvailableChecked, maxResults, minPrice, maxPrice, setProducts, setLoadingStatus, setHasSearched, onError) {
    if (checkedShops.length < 1 || !searchValue) return;
    setProducts([]);
    setLoadingStatus(LoadingStatus.LOADING);
    setHasSearched(true);
    fetchProducts(
        token,
        (data) => {
            setProducts(data.products);
            setLoadingStatus(LoadingStatus.LOADED);
        },
        (errorCode, errorDescription) => {
            setProducts([]);
            setLoadingStatus(LoadingStatus.LOADED);
            onError(errorCode, errorDescription);
        },
        searchValue, checkedShops, onlyAvailableChecked, maxResults, minPrice, maxPrice
    );
}

export default ProductSearcher;
